import {
  ChangeDetectionStrategy,
  Component,
  type OnDestroy,
  type OnInit,
  inject,
  signal,
  output,
} from '@angular/core';
import { DialogComponent, DrawerDirective } from '@bizzdesign/ui-components';
import { DynamicToolbarService } from '@bizzdesign/shared/services';
import {
  DefaultDialogSize,
  type DialogProperties,
  type ContentDetailsConfiguration,
} from '@bizzdesign/shared/models';
import { type DynamicComponent } from '@bizzdesign/shared/dom-models';
import { ContentDetailsGlobalStore } from '@bizzdesign/data-access-content-details';
import { ContentDetailsContainerComponent } from '@bizzdesign/feature-content-details';
import { ContentOverviewComponent } from '../../components/content-overview/content-overview.component';
import { HighchartsLocalStore } from '../../state/highcharts/highcharts.store';
import { HighchartsToolbarComponent } from '../../components/highcharts-toolbar/highcharts-toolbar.component';
import { toObservable } from '@angular/core/rxjs-interop';
import { AsyncPipe } from '@angular/common';
import { map, type Observable } from 'rxjs';
import { TranslocoService } from '@jsverse/transloco';
import { SharedI18nModule } from '@bizzdesign/shared/i18n';

@Component({
  selector: 'bd-highcharts-drawers-container',
  imports: [
    DrawerDirective,
    ContentDetailsContainerComponent,
    ContentOverviewComponent,
    DialogComponent,
    AsyncPipe,
    SharedI18nModule,
  ],
  templateUrl: './highcharts-drawers-container.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HighchartsDrawersContainerComponent implements OnInit, OnDestroy {
  contentDetailsClose = output<boolean>();
  contentRefresh = output<void>();
  contentItemAddTo = output<string>();
  private readonly translocoService = inject(TranslocoService);
  readonly contentDetailsGlobalStore = inject(ContentDetailsGlobalStore);
  readonly highchartsStore: HighchartsLocalStore = inject(HighchartsLocalStore);
  readonly dynamicToolbarService: DynamicToolbarService = inject(
    DynamicToolbarService,
  );
  readonly contentOverviewState = signal<'show' | 'hide'>('hide');

  toolbarComponent: DynamicComponent = {
    component: HighchartsToolbarComponent,
    inputs: {
      contentOverviewState: toObservable(this.contentOverviewState),
      onContentOverviewStateChange: (state: 'show' | 'hide') => {
        this.contentOverviewState.set(state);
      },
    },
  };

  contentDetailsConfiguration: ContentDetailsConfiguration = {
    showPublicationStatusLabel: true,
    areDetailsEditable: true,
    areAttributesEditable: true,
  };

  refreshDialogProperties$: Observable<DialogProperties> = this.translocoService
    .selectTranslateObject(
      'refreshInsightDialog',
      undefined,
      'featureHighcharts',
    )
    .pipe(
      map((translations) => {
        return {
          defaultSize: DefaultDialogSize.SMALL,
          defaultHeader: { headerTitle: translations.header },
          defaultFooter: {
            footerConfirmButtonLabel: translations.refresh,
            footerDismissButtonLabel: translations.dismiss,
          },
        };
      }),
    );

  ngOnInit(): void {
    this.dynamicToolbarService.toolbar$.next(this.toolbarComponent);
  }

  ngOnDestroy(): void {
    this.dynamicToolbarService.toolbar$.next(undefined);
  }

  onContentDetailsClose() {
    this.contentDetailsGlobalStore.updateIsDrawerOpen(false);
    this.contentDetailsClose.emit(true);
  }

  onContentOverviewClose() {
    this.contentOverviewState.set('hide');
  }

  onContentRefresh() {
    this.contentRefresh.emit();
  }
}
